<i18n
  src="@/locales/views/packing_house/production/uniq_final_packing_list/upload_packing_list.yml"
  lang="yaml"
></i18n>

<template>
  <div class="">
    <go-back-link
      :text="$t('labels.go_back_link')"
      :name="routeName('show_container_pallets')"
      :params="{
        packing_house_process_id: this.packing_house_process_id,
        container_id: this.container_id,
      }"
    >
    </go-back-link>

    <container-header
      :packing_house_process="packing_house_process"
      :container_code="container?.container_code"
    />
    <template v-for="(error, index) in errors" :key="index">
      <alert-danger :errors_data="error" />
    </template>

    <template v-if="load_general_data">
      <div class="mt-8">
        <div class="panel">
          <div class="panel-header columns-2">
            <h3 class="panel-header-title">
              {{ $t("labels.title_how_complete") }}
            </h3>
            <h3 class="text-right">
              <i
                :class="[
                  'fa-solid ',
                  open ? 'fa-chevrons-up' : 'fa-chevrons-down',
                  'fa-lg text-lime-500',
                ]"
                @click.prevent="open = !open"
              ></i>
            </h3>
          </div>
          <div class="panel-body" v-show="open">
            <div class="grid divide-y">
              <div
                v-for="(format_header, index) in format_headers"
                :key="index"
                class="grid grid-cols-3 items-center py-3"
              >
                <h3 class="font-medium">
                  {{ format_header.label }}
                  <strong class="text-red-500">
                    {{ format_header.required ? " *" : "" }}
                  </strong>
                </h3>
                <div class="col-span-2">
                  <p class="text-gray-700">
                    {{ format_header.description }}
                    {{ format_header.example }}
                    <span v-html="format_header.items_valid"> </span>
                  </p>
                </div>
              </div>
            </div>
            <div class="mt-6 border-t">
              <small class="text-red-500">{{ $t("labels.required") }}</small>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-8">
        <div class="panel">
          <div class="panel-header">
            <h3 class="panel-header-title">
              {{ $t("labels.title") }}
            </h3>
          </div>
          <div class="panel-body">
            <div class="space-y-8 divide-gray-200">
              <div class="flex sjustify-center w-full">
                <SwitchGroup>
                  <div class="flex items-center">
                    <Switch
                      v-model="replace_all"
                      :class="replace_all ? 'bg-blue-600' : 'bg-gray-200'"
                      class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    >
                      <span
                        :class="replace_all ? 'translate-x-6' : 'translate-x-1'"
                        class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                      />
                    </Switch>
                    <SwitchLabel class="ml-4">{{
                      $t("labels.replace_all")
                    }}</SwitchLabel>
                  </div>
                </SwitchGroup>
              </div>
              <div class="flex items-center justify-center w-full">
                <label
                  @dragover="dragover"
                  @dragleave="dragleave"
                  @drop="drop"
                  for="dropzone-file"
                  class="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                >
                  <div
                    class="flex flex-col items-center justify-center pt-5 pb-6"
                  >
                    <i
                      :class="[
                        'fa-solid  fa-2xl w-10 h-10 mb-3 ',
                        file?.name
                          ? 'fa-cloud-arrow-up text-lime-600'
                          : 'fa-cloud-plus text-gray-400',
                      ]"
                    ></i>
                    <p
                      v-show="!file?.name"
                      class="mb-2 text-sm text-gray-500 dark:text-gray-400"
                    >
                      <span class="font-semibold pr-1">
                        {{ $t("labels.click_to_upload") }}
                      </span>
                      <span>{{ $t("labels.drag_and_drop_file") }}</span>
                    </p>
                    <p
                      v-show="file?.name"
                      class="text-sm text-gray-500 dark:text-gray-400"
                    >
                      <span class="font-semibold">
                        {{ file?.name }}
                      </span>
                    </p>
                  </div>
                </label>
                <input
                  id="dropzone-file"
                  class="hidden"
                  type="file"
                  ref="file"
                  @change="setFile"
                />
              </div>
            </div>
          </div>
          <div class="panel-footer">
            <div class="panel-footer-actions">
              <button
                type="button"
                @click.prevent="downloadFormatXlsx"
                class="btn-white"
              >
                <i
                  :class="[
                    'fa-solid',
                    downloading ? 'fa-sync fa-spin' : 'fa-download',
                  ]"
                ></i>
                <span class="space-icon"></span
                >{{ $t("labels.download_format_button") }}
              </button>
              <span class="space-actions" />
              <success-button
                v-if="!proc_save_button"
                @click.prevent="upload()"
                :label="$t('labels.upload_button')"
              />
              <success-button-processing
                v-else
                :label="$t('labels.upload_button')"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <loading-message v-else />
  </div>
</template>

<script>
import { Switch, SwitchGroup, SwitchLabel } from "@headlessui/vue";

import Requester from "@/views/packing_house/requesters/production/uniq_final_packing_list/upload_packing_list.js";
import ContainerHeader from "@/components/views/packing_house/production/uniq_final_packing_list/ContainerHeader.vue";

export default {
  name: "UploadPackingList",
  components: {
    ContainerHeader,
    Switch,
    SwitchGroup,
    SwitchLabel,
  },
  data() {
    return {
      load_general_data: false,
      downloading: false,
      proc_save_button: false,
      packing_house_process: null,
      container: null,
      file: null,
      errors: [],
      format_headers: [],
      open: true,
      replace_all: true,
    };
  },
  props: {
    packing_house_id: { required: true },
    packing_house_process_id: {
      required: true,
    },
    container_id: {
      required: true,
    },
  },
  computed: {
    go_back_tab: function () {
      return 1;
    },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          packing_house_id: this.packing_house_id,
        },
      }).then(() => {
        this.fetchContainer();
        this.fetchFormatHeaders();
      })
    );
  },
  methods: {
    fetchPackingHouseProcess: async function () {
      let response = await Requester.fetchPackingHouseProcess({
        packing_house_process_id: this.packing_house_process_id,
      });
      this.packing_house_process = response.data;
    },
    fetchContainer: async function () {
      let response = await Requester.container({
        container_id: this.container_id,
      });
      this.container = response.data;
    },
    fetchFormatHeaders: async function () {
      let response = await Requester.fetchFormatHeaders(this.container_id);
      this.format_headers = response.data;
      this.load_general_data = true;
    },
    dragover(event) {
      event.preventDefault();
      if (!event.currentTarget.classList.contains("bg-lime-200")) {
        event.currentTarget.classList.remove("bg-gray-100");
        event.currentTarget.classList.add("bg-lime-200");
      }
    },
    dragleave(event) {
      event.currentTarget.classList.add("bg-gray-100");
      event.currentTarget.classList.remove("bg-lime-200");
    },
    drop(event) {
      event.preventDefault();
      let files = event.dataTransfer.files;
      this.file = files[0];
      event.currentTarget.classList.add("bg-gray-100");
      event.currentTarget.classList.remove("bg-lime-200");
    },
    setFile: function () {
      if (!this.$refs.file) return;
      this.file = this.$refs.file.files[0];
    },
    async upload() {
      this.proc_save_button = true;
      try {
        const formData = new FormData();
        formData.append("file", this.file);
        formData.append("replace_all", this.replace_all);
        let response = await Requester.uploadPackingList(
          this.container_id,
          formData
        );

        if (response.status == 200) {
          this.registerSuccessNotification({
            title: response.data.success_message,
          });
        }
        this.goToContainer();
      } catch (error) {
        if (error.response.status === 422) {
          this.errors = error.response.data.map((e) => {
            return {
              display: true,
              title: e.error_title,
              messages: e.error_messages,
            };
          });
        }
      }
      this.$refs.file.value = null;
      this.file = this.$refs.file.files[0];
      this.proc_save_button = false;
    },
    downloadFormatXlsx: async function () {
      this.downloading = true;
      try {
        let response = await Requester.downloadFormatXlsx(this.container_id);

        const file = response.data;
        var fileURL = window.URL.createObjectURL(new Blob([file]));
        var fileLink = document.createElement("a");

        fileLink.href = fileURL;
        fileLink.setAttribute("download", `Formato Packing List.xlsx`);
        document.body.appendChild(fileLink);

        fileLink.click();
      } catch (error) {
        error;
      }
      this.downloading = false;
    },
    goToContainer() {
      this.$router.push({
        name: this.routeName("show_container_pallets"),
        params: {
          container_id: this.container_id,
          packing_house_process_id: this.packing_house_process_id,
        },
      });
    },
  },
};
</script>
